import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import API_URL from '../../config';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // Target destination after login (deep link preservation)
  const from = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/gestionShifts';

  // If already authenticated and done initializing, redirect to destination
  if (!isAuthLoading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email est requis';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email est invalide';
    
    if (!password) newErrors.password = 'Mot de passe est requis';
    else if (password.length < 6) newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    setIsLoading(true);
    setErrors({});
  
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe })
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Email ou mot de passe incorrect.');
      }
  
      const data = await response.json();
      await login(data.access_token, rememberMe);
      navigate(from, { replace: true });
      
    } catch (error) {
      console.error('❌ Erreur de connexion :', error);
      setErrors({ api: error.message || 'Email ou mot de passe incorrect.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setErrors({ email: 'Veuillez entrer votre email pour réinitialiser le mot de passe' });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Email est invalide' });
      return;
    }
  
    setIsLoading(true);
    setErrors({});
    setForgotMessage('');
  
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Erreur lors de la demande');
      }
  
      const data = await response.json();
      setForgotMessage(data.message || 'Si votre email existe, vous recevrez un lien de réinitialisation.');
      
    } catch (error) {
      console.error('❌ Erreur mot de passe oublié :', error);
      setErrors({ api: error.message || 'Une erreur est survenue.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="p-6 sm:p-8">
            {/* Header logo & title */}
            <div className="text-center mb-6">
              <img 
                src="/logo.png" 
                alt="PlannMe Logo" 
                className="w-12 h-12 rounded-full mx-auto mb-3 shadow-md"
              />
              <h2 className="text-2xl sm:text-3xl font-black text-gray-800 tracking-tight">
                {isForgotPassword ? "Mot de passe oublié ?" : "Connectez-vous"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {isForgotPassword 
                  ? "Entrez votre email pour recevoir un lien de réinitialisation." 
                  : "Entrez vos informations pour accéder à votre espace."}
              </p>
            </div>

            {errors.api && (
              <div className="mb-4 p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-2">
                <span className="font-semibold">Erreur :</span>
                <span>{errors.api}</span>
              </div>
            )}
            
            {forgotMessage && (
              <div className="mb-4 p-3.5 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
                {forgotMessage}
              </div>
            )}

            {!isForgotPassword ? (
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Adresse Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 text-base rounded-xl border ${errors.email ? 'border-red-400 bg-red-50/30' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
                    placeholder="votre@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Mot de passe
                    </label>
                    <button 
                      type="button" 
                      onClick={() => { setIsForgotPassword(true); setErrors({}); setForgotMessage(''); }}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 focus:outline-none"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 text-base rounded-xl border ${errors.password ? 'border-red-400 bg-red-50/30' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
                    placeholder="••••••••"
                  />
                  {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
                </div>

                {/* Remember Me Checkbox - Touch-friendly on mobile */}
                <div className="mb-6">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none py-1">
                    <input
                      id="rememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 cursor-pointer transition-colors"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Se souvenir de moi (Rester connecté)
                    </span>
                  </label>
                  <p className="text-[11px] text-gray-400 ml-6.5">
                    {rememberMe 
                      ? "Votre session restera active sur cet appareil." 
                      : "La session sera fermée à la fermeture du navigateur."}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white font-semibold rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all text-base ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? "Connexion en cours..." : "Se connecter"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleForgotPassword} noValidate>
                <div className="mb-6">
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Adresse Email
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 text-base rounded-xl border ${errors.email ? 'border-red-400 bg-red-50/30' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
                    placeholder="votre@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white font-semibold rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all text-base mb-4 ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? "Envoi en cours..." : "Envoyer le lien"}
                </button>
                
                <div className="text-center">
                  <button 
                    type="button" 
                    onClick={() => { setIsForgotPassword(false); setErrors({}); setForgotMessage(''); }}
                    className="text-sm text-gray-600 hover:text-indigo-600 font-medium focus:outline-none py-1"
                  >
                    ← Retour à la connexion
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
